import { enUS as en, ru } from 'date-fns/locale';

import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import dateFnsParse from 'date-fns/parse';
import dateFnsFormat from 'date-fns/format';
import intervalToDuration from 'date-fns/intervalToDuration';
import differenceInYears from 'date-fns/differenceInYears';
import subSeconds from 'date-fns/subSeconds';

import { getLanguage, TranslatorFunc } from '$translation';

export const dateFnsLocale: Record<string, any> = {
  ru,
  en,
};

export const getLocale = () => {
  const locale = getLanguage();
  return dateFnsLocale[locale] ?? en;
};

export const format = (date: number | Date, formatString: string, options?: any) =>
  dateFnsFormat(date, formatString, {
    locale: getLocale(),
    ...options,
  });

export function formatMsk(ts: number) {
  if (!ts) {
    return '';
  }
  const text = new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(ts * 1000));
  return `${text} МСК`;
}

export const parse = (dateString: string, formatString: string, fallbackDate: Date) =>
  dateFnsParse(dateString, formatString, fallbackDate, {
    locale: getLocale(),
  });

export const birthdayToAge = (bDate: string) => {
  if (!bDate) {
    return 0;
  }

  try {
    const ageTs = parse(bDate, 'd.m.yyyy', new Date()).getTime() || 0;
    if (!ageTs) {
      return 0;
    }
    return getUserAge(ageTs) || 0;
  } catch {
    return 0;
  }
};

export const getUserAge = (ts: number) => {
  const birthday = new Date(ts);
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export function formatDate(date: Date, t: TranslatorFunc) {
  const now = new Date();
  const isYearAndMonthEqual =
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  const daysDiff = Math.abs(date.getDate() - now.getDate());
  if (daysDiff < 1 && isYearAndMonthEqual) {
    return t('today');
  } else if (daysDiff < 2 && isYearAndMonthEqual) {
    return t('yesterday');
  } else {
    if (differenceInYears(now, date) < 1) {
      return format(date, 'd MMMM');
    } else {
      return format(date, 'd MMMM yyyy');
    }
  }
}

export function getDuration(durationTimeStamp: number) {
  const durationObject = intervalToDuration({
    start: new Date(new Date().getTime() - durationTimeStamp * 1000),
    end: new Date(),
  });

  const { seconds, minutes, hours, days, weeks, months, years } = durationObject;

  if (years) return { name: 'years', count: years };
  if (months) return { name: 'months', count: months };
  if (weeks) return { name: 'weeks', count: weeks };
  if (days) return { name: 'days', count: days };
  if (hours) return { name: 'hours', count: hours };
  if (minutes) return { name: 'minutes', count: minutes };
  return { name: 'seconds', count: seconds };
}

export const normalizeDateRange = (range: [Date, Date]): [Date, Date] => {
  return [startOfDay(range[0]), subSeconds(endOfDay(range[1]), 1)];
};
